import { useState } from 'react';
import { enterSampleMode, saveDeviceAuth, signedIn } from '../../data/session';
import { configureApi } from '../../lib/api';
import { appVersion } from '../../lib/platform';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import s from './Auth.module.css';
import { AuthLayout } from './AuthLayout';

/** Parses `tripsapp://pair?server=...&code=...` from the QR code. */
function parsePairing(text: string): { server: string; code: string } | null {
  try {
    const u = new URL(text);
    const server = u.searchParams.get('server');
    const code = u.searchParams.get('code');
    return server && code ? { server, code } : null;
  } catch {
    return null;
  }
}

/**
 * Opens Google's scanner (from Play services: no camera permission, nothing bundled in the APK).
 * Resolves null when cancelled.
 */
async function scanQr(onPreparing: () => void): Promise<string | null> {
  const {
    BarcodeScanner,
    BarcodeFormat,
    GoogleBarcodeScannerModuleInstallState: S,
  } = await import('@capacitor-mlkit/barcode-scanning');
  const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
  if (!available) {
    onPreparing();
    try {
      await new Promise<void>((resolve, reject) => {
        void BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', (ev) => {
          if (ev.state === S.COMPLETED) resolve();
          else if (ev.state === S.FAILED || ev.state === S.CANCELED)
            reject(new Error("Couldn't get the scanner from Google Play. Type the code instead."));
        });
        BarcodeScanner.installGoogleBarcodeScannerModule().catch(reject);
      });
    } finally {
      await BarcodeScanner.removeAllListeners();
    }
  }
  try {
    const { barcodes } = await BarcodeScanner.scan({ formats: [BarcodeFormat.QrCode] });
    return barcodes[0]?.rawValue ?? null;
  } catch (e) {
    if (/cancel/i.test((e as Error).message)) return null;
    throw e;
  }
}

export function PairScreen() {
  const [server, setServer] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const pair = async (srv: string, c: string) => {
    setBusy(true);
    setError(null);
    try {
      const base = srv.replace(/\/+$/, '');
      const res = await fetch(`${base}/api/devices/pair`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          code: c,
          name: 'Galaxy S26 Ultra',
          platform: 'android',
          appVersion,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        token?: string;
        deviceId?: string;
        error?: string;
      };
      if (!res.ok || !data.token || !data.deviceId)
        throw new Error(data.error ?? "That code didn't work. Show a new code on your computer.");
      await saveDeviceAuth({ server: base, token: data.token, deviceId: data.deviceId });
      configureApi({ base, token: data.token });
      await signedIn();
    } catch (e) {
      setError(
        (e as Error).message === 'Failed to fetch'
          ? "Couldn't reach the server. Check your connection and the address."
          : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Pair this phone">
      <p className="muted">
        On your computer, open Settings, then Pair a phone, and scan the code it shows.
      </p>
      <Button
        variant="primary"
        icon="qr-code"
        wide
        disabled={busy || preparing}
        onClick={async () => {
          setError(null);
          try {
            const text = await scanQr(() => setPreparing(true));
            setPreparing(false);
            if (text === null) return;
            const p = parsePairing(text);
            if (!p) {
              setError("That isn't a pairing code. Scan the code from Settings on your computer.");
              return;
            }
            await pair(p.server, p.code);
          } catch (e) {
            setPreparing(false);
            setError((e as Error).message);
          }
        }}
      >
        {preparing ? 'Getting the scanner ready' : 'Scan code'}
      </Button>
      <form
        className={s.steps}
        onSubmit={(e) => {
          e.preventDefault();
          void pair(server.startsWith('http') ? server : `https://${server}`, code);
        }}
      >
        <TextField
          label="Or type the server"
          value={server}
          onChange={setServer}
          placeholder="windowseat.dev"
          inputMode="url"
          autoCapitalize="none"
        />
        <TextField
          label="And the code"
          value={code}
          onChange={(v) => setCode(v.toUpperCase())}
          placeholder="ABCDE-FGHJK"
          autoCapitalize="characters"
        />
        {error && <p className={`${s.error} t-footnote`}>{error}</p>}
        <Button
          type="submit"
          wide
          disabled={busy || !server || code.replace(/[^A-Z2-7]/g, '').length < 10}
        >
          Pair
        </Button>
      </form>
      <button type="button" className={s.link} onClick={() => enterSampleMode()}>
        Look around with sample trips
      </button>
    </AuthLayout>
  );
}
